import { useId, useState } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/components/form/FieldError';
import { PdfDocumentRow } from '@/components/form/PdfDocumentRow';
import { uploadFile } from '@/api/service/upload';
import { getApiErrorMessage } from '@/api/errorHandler';
import { DELETE_BUTTON_CLASS, LABEL_CLASS } from '@/lib/styles';

export interface UploadedFile {
  name: string;
  url: string;
}

interface PdfUploadFieldProps {
  label: string;
  /** Caption under the file name once uploaded, e.g. "Signed loan agreement". */
  caption: string;
  value: UploadedFile | null;
  onChange: (file: UploadedFile | null) => void;
  error?: string;
}

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Uploads a PDF as soon as it is picked and hands back its URL. */
export function PdfUploadField({ label, caption, value, onChange, error }: PdfUploadFieldProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (!isPdf(file)) {
      setUploadError('Only PDF files are allowed');
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const response = await uploadFile(file);
      onChange({ name: response.filename || file.name, url: response.url });
    } catch (uploadFailure) {
      setUploadError(getApiErrorMessage(uploadFailure, 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className={LABEL_CLASS}>{label}</Label>

      {value ? (
        <PdfDocumentRow
          name={value.name}
          url={value.url}
          caption={caption}
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={DELETE_BUTTON_CLASS}
              onClick={() => onChange(null)}
            >
              Remove
            </Button>
          }
        />
      ) : (
        <label
          htmlFor={inputId}
          className="block cursor-pointer rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/50 p-5 text-center transition-colors hover:border-[#4369a0]/30 hover:bg-[#4369a0]/5"
        >
          <input
            id={inputId}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            disabled={uploading}
            onChange={(event) => {
              void handleFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          <p className="text-sm font-medium text-gray-700">
            {uploading ? 'Uploading...' : 'Click to upload signed document'}
          </p>
          <p className="mt-1 text-xs text-gray-500">PDF only</p>
        </label>
      )}

      <FieldError message={uploadError || error} />
    </div>
  );
}
